import { createTheme, darken, lighten } from '@mui/material/styles';
import getTheme from '../../theme';

// Warm, minimal theme scoped to the live-quiz screens (host + student) only —
// the rest of the site keeps its black/white brand.
//
// Built via createTheme(baseTheme, overrides) rather than handed to a nested
// ThemeProvider as a raw object: MUI's nested-provider merge is a *shallow*
// Object.assign, not a deep merge, so a raw `palette` override would wipe out
// `palette.action`/`palette.grey`/`getContrastText` that hover, selected, and
// disabled states depend on internally. Running it through createTheme here
// recomputes all of that correctly, and the result is a complete theme object
// the nested provider can safely swap in wholesale.
//
// Light mode: #FF6B4A is the "coral" accent but it fails WCAG AA against white
// text (2.82:1), so it's reserved for things like borders/hover glows; anywhere
// text sits on a solid fill (buttons, the selected-answer highlight) uses the
// darker #CC4530 shade instead (4.71:1).
// Dark mode: the coral is lightened instead (so it still reads as "coral" on a
// near-black background) and paired with dark contrastText, since the lightened
// shade is itself light enough that white-on-coral would fail contrast.
// theme.js already ran responsiveFontSizes on baseTheme, and typography isn't
// touched by these overrides, so createTheme carries the responsive variants
// through unchanged — a second responsiveFontSizes pass here would just
// recompute the same breakpoint-keyed entries for no behavioral gain.
// ListItemButton (answerable) and ListItem (read-only) render the same
// options list, just with/without a click handler — one override object
// shared by both keeps their radius from drifting apart.
const roundedItem = { styleOverrides: { root: { borderRadius: 14 } } };

const livePalettes = {
    light: {
        mode: 'light',
        background: {
            default: '#FFF8F0',
            paper: '#FFFFFF',
        },
        primary: {
            main: '#CC4530',
            light: '#FF6B4A',
            contrastText: '#FFFFFF',
        },
        success: {
            main: '#2E7D32',
            dark: '#1B5E20',
            contrastText: '#FFFFFF',
        },
        error: {
            main: '#C62828',
            dark: '#8E1E1E',
            contrastText: '#FFFFFF',
        },
        text: {
            primary: '#2A2019',
            secondary: '#7A6A5C',
        },
        divider: '#F0E0D2',
        code: {
            panel: '#F3E4D3',
        },
    },
    dark: {
        mode: 'dark',
        background: {
            default: '#1E1712',
            paper: '#26201A',
        },
        primary: {
            main: '#FF7A5C',
            light: '#FF9478',
            contrastText: '#1E1712',
        },
        success: {
            main: '#4CAF50',
            dark: '#2E7D32',
            contrastText: '#FFFFFF',
        },
        error: {
            main: '#E57373',
            dark: '#C62828',
            contrastText: '#FFFFFF',
        },
        text: {
            primary: '#F0E4D8',
            secondary: '#B8A796',
        },
        divider: 'rgba(240, 224, 210, 0.16)',
        code: {
            panel: '#3A2E22',
        },
    },
};

export default function getLiveTheme(mode) {
    const base = getTheme(mode);
    const palette = livePalettes[mode] || livePalettes.light;

    return createTheme(base, {
        palette,
        shape: {
            borderRadius: 14,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 14,
                        padding: '10px 28px',
                        boxShadow: 'none',
                        fontWeight: 700,
                    },
                    contained: {
                        '&:hover': {
                            boxShadow: 'none',
                            backgroundColor:
                                mode === 'dark'
                                    ? lighten(palette.primary.main, 0.15)
                                    : darken(palette.primary.main, 0.1),
                        },
                    },
                    outlined: {
                        borderWidth: '2px',
                        '&:hover': {
                            borderWidth: '2px',
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        border: 'none',
                        boxShadow:
                            mode === 'dark'
                                ? '0 2px 14px rgba(0, 0, 0, 0.4)'
                                : '0 2px 14px rgba(42, 32, 25, 0.08)',
                    },
                    rounded: {
                        borderRadius: 16,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        fontWeight: 600,
                    },
                },
            },
            MuiListItemButton: roundedItem,
            MuiListItem: roundedItem,
        },
    });
}
